import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, TrendingUp, TrendingDown, Minus, Calendar, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { api, storage } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { toast } from "sonner";

export default function History() {
  const navigate = useNavigate();
  const { lang, lp, t } = useLang();
  const [assessments, setAssessments] = useState([]);
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(true);

  const user = storage.getUser();

  useEffect(() => {
    if (!user) {
      navigate(lp("/profile"));
      return;
    }

    const loadHistory = async () => {
      try {
        const data = await api.getUserAssessments(user.id);
        setAssessments(data.assessments);

        if (data.assessments.length >= 2) {
          const compData = await api.compareAssessments(
            data.assessments[0].id,
            data.assessments[1].id,
            lang
          );
          setComparison(compData);
        }
      } catch (error) {
        console.error(error);
        toast.error(t.history.errLoad);
      } finally {
        setLoading(false);
      }
    };

    loadHistory();
  }, [navigate, lang]);

  const handleRetake = () => {
    storage.clearAssessmentProgress();
    navigate(lp("/assessment/1"));
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString(lang === "ru" ? "ru-RU" : "en-US", {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const DeltaIndicator = ({ delta }) => {
    if (delta > 0) {
      return (
        <span className="flex items-center text-[#00E676]">
          <TrendingUp className="w-4 h-4 mr-1" />
          +{delta}
        </span>
      );
    } else if (delta < 0) {
      return (
        <span className="flex items-center text-[#FF1744]">
          <TrendingDown className="w-4 h-4 mr-1" />
          {delta}
        </span>
      );
    }
    return (
      <span className="flex items-center text-white/40">
        <Minus className="w-4 h-4 mr-1" />
        0
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#222222] text-white flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-[#FFEA00] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#222222] text-white">
      {/* Header */}
      <header className="px-6 py-4 flex justify-between items-center max-w-5xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate(lp("/"))}
          className="text-white/60 hover:text-white"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t.common.home}
        </Button>
        <div className="flex items-center gap-2">
          <LanguageSwitcher />
          <Button
            onClick={handleRetake}
            className="bg-[#FFEA00] hover:bg-[#FFD600] text-black font-semibold"
            data-testid="retake-btn"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            {t.history.retake}
          </Button>
        </div>
      </header>

      <main className="px-6 py-8 max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {t.history.title}
          </h1>
          <p className="text-white/60">
            {t.history.subtitle(user?.name)}
          </p>
        </motion.div>

        {assessments.length === 0 ? (
          <Card className="bg-[#2a2a2a] border-white/5">
            <CardContent className="p-12 text-center">
              <Calendar className="w-12 h-12 text-white/20 mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">{t.history.noneTitle}</h3>
              <p className="text-white/60 mb-6">{t.history.noneDesc}</p>
              <Button
                onClick={() => navigate(lp("/assessment/1"))}
                className="bg-[#FFEA00] hover:bg-[#FFD600] text-black font-semibold"
              >
                {t.history.startAssessment}
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Comparison Section */}
            {comparison && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="mb-12"
              >
                <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {t.history.latestVsPrevious}
                </h2>

                <div className="grid md:grid-cols-3 gap-6 mb-8">
                  {/* Overall Score Comparison */}
                  <Card className="bg-[#2a2a2a] border-white/5 col-span-1">
                    <CardContent className="p-6 text-center">
                      <p className="text-white/60 mb-2">{t.history.overallProgress}</p>
                      <div className="text-4xl font-bold mb-2">
                        <DeltaIndicator delta={comparison.overall_delta} />
                      </div>
                      <p className="text-white/40 text-sm">
                        {comparison.latest.overall_score} vs {comparison.previous.overall_score}
                      </p>
                    </CardContent>
                  </Card>

                  <Card className="bg-[#2a2a2a] border-white/5 col-span-1">
                    <CardContent className="p-6 text-center">
                      <p className="text-white/60 mb-2">{t.history.percentageChange}</p>
                      <div className="text-4xl font-bold text-[#FFEA00] mb-2">
                        {comparison.percentage_delta > 0 ? '+' : ''}{comparison.percentage_delta}%
                      </div>
                      <p className="text-white/40 text-sm">
                        {comparison.latest.overall_percentage}% vs {comparison.previous.overall_percentage}%
                      </p>
                    </CardContent>
                  </Card>

                  <Card className="bg-[#2a2a2a] border-white/5 col-span-1">
                    <CardContent className="p-6 text-center">
                      <p className="text-white/60 mb-2">{t.history.areasImproved}</p>
                      <div className="text-4xl font-bold text-[#00E676] mb-2">
                        {comparison.deltas.filter(d => d.delta > 0).length}
                      </div>
                      <p className="text-white/40 text-sm">{t.history.ofAreas}</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Per-Area Deltas */}
                <Card className="bg-[#2a2a2a] border-white/5">
                  <CardHeader>
                    <CardTitle>{t.history.areaProgress}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {comparison.deltas.map(area => (
                        <div key={area.area_id} className="flex items-center gap-4">
                          <div
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: area.color }}
                          />
                          <span className="flex-1">{area.area_name}</span>
                          <span className="text-white/60 w-20 text-right">
                            {area.score_before} → {area.score_after}
                          </span>
                          <div className="w-24 text-right">
                            <DeltaIndicator delta={area.delta} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {/* Assessment List */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {t.history.allAssessments}
              </h2>

              <div className="space-y-4">
                {assessments.map((assessment, idx) => (
                  <Card
                    key={assessment.id}
                    className="bg-[#2a2a2a] border-white/5 hover:border-white/10 transition-colors cursor-pointer"
                    onClick={() => navigate(lp(`/results/${assessment.id}`))}
                  >
                    <CardContent className="p-4 flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-white/40">
                          {assessments.length - idx}
                        </div>
                        <div>
                          <p className="font-semibold">
                            {formatDate(assessment.created_at)}
                            {idx === 0 && <span className="ml-2 text-xs text-[#FFEA00]">{t.history.latest}</span>}
                          </p>
                          <p className="text-sm text-white/50">
                            {t.history.score(assessment.overall_score, assessment.overall_percentage)}
                          </p>
                        </div>
                      </div>
                      <ArrowRight className="w-5 h-5 text-white/40" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            </motion.div>
          </>
        )}
      </main>
    </div>
  );
}
